import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { DateInput } from '@/common/components/DateInput/DateInput'
import RadioCards from '@/common/components/RadioCards'
import SelectController from '@/common/components/SelectController'
import Switch from '@/common/components/Switch'
import TextField from '@/common/components/TextField'
import { UnitTextField } from '@/common/components/UnitTextField'
import CreateForwardingDirectiveEntryFormStepHeader from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/components/CreateForwardingDirectiveEntryFormStepHeader'
import EstablishmentChoiceList from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/components/directivesFormSteps/EstablishmentChoiceEntryFormStep/EstablishmentChoiceList'
import { establishmentChoiceEntryOptions } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/establishmentChoiceEntry/establishmentChoiceEntryOptions.constant'
import { MaintenanceForwardingsEstablishmentChoiceEntryStepOptions } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsShowDirectiveDetails.type'
import { MaintenanceForwardingsDirectiveFromSteps } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsShowDirectiveDetails.type'
import { useCreateForwardingFormStepValidation } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/hooks/useCreateForwardingFormStepValidation'
import { EstablishmentChoiceEntryAuctionEntrySchema } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/schemas/maintenanceForwardingDirectiveSteps.schema'
import { useCreateForwardingsFormStore } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/stores/createForwardingsFormStore'
import { auctionEntryKindOptions } from '@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/constants/auctionEntryKind.constant'
import { ClockIcon } from 'lucide-react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

const EstablishmentChoiceEntryAuctionEntryPending = () => {
  const {
    increaseStep,
    selectedEstablishments,
    appendSelectedEstablishment,
    removeSelectedEstablishment,
  } = useCreateForwardingsFormStore()

  const { control, setValue } = useFormContext<EstablishmentChoiceEntryAuctionEntrySchema>()

  const { establishment_choice_entry_attributes: watchEstablishmentChoiceEntryAttributes } = useWatch({
    control,
  })

  const { isValid } = useCreateForwardingFormStepValidation(
    MaintenanceForwardingsDirectiveFromSteps.EstablishmentChoiceEntry,
    // Stable, single-element dependency: re-validates when any attribute changes without varying the
    // deps-array size between renders (which React forbids).
    [JSON.stringify(watchEstablishmentChoiceEntryAttributes ?? {})]
  )

  const auctionEntryOption =
    establishmentChoiceEntryOptions[MaintenanceForwardingsEstablishmentChoiceEntryStepOptions.AuctionEntry]!

  return (
    <div className="flex flex-col gap-4">
      <CreateForwardingDirectiveEntryFormStepHeader
        goBackAction={() => {
          setValue('establishment_choice_entry_type', undefined!)
          setValue('establishment_choice_entry_attributes', undefined!)
        }}
        title={auctionEntryOption.title}
        description={auctionEntryOption.description()}
      />

      <div className="flex flex-col gap-4">
        <SelectController
          control={control}
          name="establishment_choice_entry_attributes.kind"
          options={auctionEntryKindOptions}
          label="Tipo de leilão"
          markAsRequired
        />

        <div className="flex gap-3">
          <Controller
            control={control}
            name="establishment_choice_entry_attributes.minimum_of_proposals_to_finish"
            render={({ field }) => (
              <UnitTextField
                label="Mínimo de propostas para arremate"
                markAsRequired
                unit="Propostas"
                decimals={0}
                className="flex-1"
                forceValue
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name="establishment_choice_entry_attributes.allowance_radius_in_kilometers"
            render={({ field }) => (
              <UnitTextField
                label="Raio de exibição do leilão"
                markAsRequired
                unit="Km"
                decimals={0}
                className="flex-1"
                forceValue
                {...field}
              />
            )}
          />
        </div>

        <Box className="flex flex-col gap-4 p-4">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => {
              setValue(
                'establishment_choice_entry_attributes.wip_schedule',
                !watchEstablishmentChoiceEntryAttributes?.wip_schedule
              )
            }}
          >
            <Switch
              className="h-8 w-14 border px-[3px] data-[state=unchecked]:border-white-100 data-[state=unchecked]:bg-black-900 data-[state=checked]:border-blue-800 data-[state=checked]:bg-primary-blue"
              thumbClassName="size-6 border transition-transform data-[state=checked]:translate-x-6 data-[state=checked]:bg-white-800 data-[state=checked]:border-blue-800 data-[state=unchecked]:border-black-300"
              checked={!!watchEstablishmentChoiceEntryAttributes?.wip_schedule}
              onClick={(event) => event.stopPropagation()}
              onCheckedChange={(value) => setValue('establishment_choice_entry_attributes.wip_schedule', value)}
            />

            <p className="text-sm flex-1">
              Agendar início de leilão (Se não agendado, iniciará imediatamente)
            </p>
          </div>

          {watchEstablishmentChoiceEntryAttributes?.wip_schedule && (
            <div className="flex gap-3">
              <DateInput
                fieldName="establishment_choice_entry_attributes.starts_at_date"
                control={control}
                value={watchEstablishmentChoiceEntryAttributes?.starts_at_date}
                label="Data de início"
                className="flex-1"
                markAsRequired
                minDate={new Date()}
                handleChange={(value) => setValue('establishment_choice_entry_attributes.starts_at_date', value)}
              />

              <Controller
                control={control}
                name="establishment_choice_entry_attributes.starts_at_time"
                render={({ field }) => (
                  <TextField
                    label="Horário de início"
                    type="time"
                    markAsRequired
                    className="flex-1"
                    LeftIcon={<ClockIcon className="w-5 h-5 text-white-400" />}
                    {...field}
                  />
                )}
              />
            </div>
          )}
        </Box>

        <div className="flex gap-3">
          <DateInput
            fieldName="establishment_choice_entry_attributes.finishes_at_date"
            control={control}
            value={watchEstablishmentChoiceEntryAttributes?.finishes_at_date}
            label="Data de encerramento"
            className="flex-1"
            markAsRequired
            minDate={new Date()}
            handleChange={(value) => setValue('establishment_choice_entry_attributes.finishes_at_date', value)}
          />

          <Controller
            control={control}
            name="establishment_choice_entry_attributes.finishes_at_time"
            render={({ field }) => (
              <TextField
                label="Horário de encerramento"
                type="time"
                markAsRequired
                className="flex-1"
                LeftIcon={<ClockIcon className="w-5 h-5 text-white-400" />}
                {...field}
              />
            )}
          />
        </div>
      </div>

      {(!watchEstablishmentChoiceEntryAttributes?.will_select_allowed_establishments ||
        watchEstablishmentChoiceEntryAttributes?.will_select_allowed_establishments === '0') && (
        <RadioCards
          options={[
            {
              value: '0',
              title: 'Todos estabelecimentos aptos',
              description:
                'Todos Estabelecimentos homologados para seu Equipamento a partir de um raio de X Km do mesmo podem realizar lances.',
            },
            {
              value: '1',
              title: 'Escolher participantes',
              description: 'Defina quais Estabelecimentos homologados podem realizar lances.',
            },
          ]}
          value={watchEstablishmentChoiceEntryAttributes?.will_select_allowed_establishments}
          onValueChange={(value) => {
            setValue('establishment_choice_entry_attributes.will_select_allowed_establishments', value)
          }}
        />
      )}

      {watchEstablishmentChoiceEntryAttributes?.will_select_allowed_establishments === '1' && (
        <Box className="flex flex-col gap-4 p-4">
          <CreateForwardingDirectiveEntryFormStepHeader
            goBackAction={() => {
              setValue('establishment_choice_entry_attributes.will_select_allowed_establishments', undefined!)
            }}
            title="Escolher participantes"
            description="Defina quais Estabelecimentos homologados podem realizar lances."
          />

          <EstablishmentChoiceList
            selectedEstablishmentsIds={selectedEstablishments.map(({ establishment_id }) => establishment_id)}
            onSelectEstablishment={(establishment) => {
              const currentIds = watchEstablishmentChoiceEntryAttributes?.allowed_establishment_ids ?? []
              const isSelected = currentIds.includes(establishment.establishment_id)

              if (isSelected) {
                removeSelectedEstablishment(establishment.id)
                setValue(
                  'establishment_choice_entry_attributes.allowed_establishment_ids',
                  currentIds.filter((id) => id !== establishment.establishment_id)
                )
              } else {
                appendSelectedEstablishment(establishment)
                setValue('establishment_choice_entry_attributes.allowed_establishment_ids', [
                  ...currentIds,
                  establishment.establishment_id,
                ])
              }
            }}
          />
        </Box>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm">
          {watchEstablishmentChoiceEntryAttributes?.will_select_allowed_establishments === '1' &&
            (selectedEstablishments.length > 0
              ? `${selectedEstablishments.length} Estabelecimento${selectedEstablishments.length !== 1 ? 's' : ''} selecionado${selectedEstablishments.length !== 1 ? 's' : ''}`
              : 'Nenhum estabelecimento selecionado')}
        </p>

        <div className="flex items-center gap-2">
          <Button
            variant="error"
            onClick={() => {
              setValue('establishment_choice_entry_type', undefined!)
              setValue('establishment_choice_entry_attributes', undefined!)
            }}
          >
            Cancelar
          </Button>

          <Button
            onClick={increaseStep}
            disabled={
              !isValid ||
              (watchEstablishmentChoiceEntryAttributes?.will_select_allowed_establishments === '1' &&
                selectedEstablishments.length === 0)
            }
          >
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  )
}

export default EstablishmentChoiceEntryAuctionEntryPending
